import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../api";
import Layout from "../components/Layout";
import Pagination from "../components/Pagination";

import {
  FiTrash2,
  FiEdit,
  FiEye,
  FiCreditCard,
  FiPlus,
  FiSave,
  FiSearch,
} from "react-icons/fi";

import { toasts } from "../components/AppToast";


const EMPTY_FORM = {
  cat_id: "",
  carmodel_id: "",
  season_id: "",
  price: "",
  useDefault: false,
  duration_prices: [],
};

export default function CarPrice() {
  const navigate = useNavigate();

  // Dropdown data
  const [groups, setGroups] = useState([]);
  const [cars, setCars] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [rentDurs, setRentDurs] = useState([]);

  const [filterCatId, setFilterCatId] = useState("");
  const [filterSeasonId, setFilterSeasonId] = useState("");

  // Price records
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [carsLoading, setCarsLoading] = useState(false);

  // Modals
  const [viewModal, setViewModal] = useState(null);
  const [delModal, setDelModal] = useState(null);
  const [delSaving, setDelSaving] = useState(false);
  const [formModal, setFormModal] = useState(false);

  // Search / Pagination
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const limit = 10;

  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // AUTH

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/login");
    }
  }, [navigate]);

  // FETCH DROPDOWN DATA

  const fetchMeta = useCallback(async () => {
    try {
      const [g, s, rd] = await Promise.all([
        API.get("/price/cat"),
        API.get("/price/seasons"),
        API.get("/price/rent-durations"),
      ]);

      const groupsData = g.data || [];
      const seasonsData = s.data || [];
      const rentData = rd.data || [];

      setGroups(groupsData);
      setSeasons(seasonsData);
      setRentDurs(rentData);

      setForm((prev) => ({
        ...prev,
        duration_prices: rentData.map((d) => ({
          RentslabId: d.RentslabId,
          rentslab_price: "",
        })),
      }));
    } catch (error) {
      console.error("fetchMeta error:", error);

      toasts.error(error.response?.data?.message || "Failed to load form data");
    }
  }, []);

  // INITIAL LOAD

  useEffect(() => {
    fetchMeta();
  }, [fetchMeta]);

  // FETCH PRICE DATA

  const fetchPrices = useCallback(async () => {
    setLoading(true);

    try {
      const response = await API.get("/price", {
        params: {
          page,
          limit,
          search: search.trim(),
          cat_id: filterCatId,
          season_id: filterSeasonId,
        },
      });

      const responseData = response.data;

      setPrices(responseData.data || []);
      setTotal(Number(responseData.total || 0));
      setTotalPages(Number(responseData.totalPages || 0));
    } catch (error) {
      console.error("fetchPrices error:", error);

      toasts.error(error.response?.data?.message || "Failed to load prices");
    } finally {
      setLoading(false);
    }
  }, [page, search, filterCatId, filterSeasonId]);

  // LOAD PRICE DATA WHEN FILTER/PAGE/SEARCH CHANGES

  useEffect(() => {
    fetchPrices();
  }, [fetchPrices]);

  // LOAD CARS

  const loadCars = useCallback(async (catId) => {
    if (!catId) {
      setCars([]);
      return;
    }

    setCarsLoading(true);

    try {
      const response = await API.get(`/price/cars/${catId}`);

      setCars(response.data || []);
    } catch (error) {
      console.error("loadCars error:", error);

      toasts.error("Failed to load cars");
    } finally {
      setCarsLoading(false);
    }
  }, []);

  // RESET FORM

  const resetForm = useCallback(() => {
    setForm({
      ...EMPTY_FORM,
      duration_prices: rentDurs.map((d) => ({
        RentslabId: d.RentslabId,
        rentslab_price: "",
      })),
    });

    setEditId(null);
    setCars([]);
  }, [rentDurs]);

  const openAddModal = () => {
    resetForm();
    setFormModal(true);
  };

  const closeFormModal = () => {
    if (saving) return;

    setFormModal(false);
    resetForm();
  };

  // EDIT

  const startEdit = async (price) => {
    try {
      setFormModal(true);

      await loadCars(price.cat_id);

      const response = await API.get(`/price/${price.tar_id}`);

      const rec = response.data;

      const catId = rec.cat_id ?? price.cat_id;
      const carId = rec.carmodel_id ?? price.carmodel_id;
      const seasonId = rec.season_id ?? price.season_id;

      const basePrice = rec.price_perday ?? rec.price ?? price.price_perday;

      const existingDurations = rec.duration_prices || [];

      const durationPrices = rentDurs.map((rd) => {
        const found = existingDurations.find(
          (dp) =>
            Number(dp.Rentslabid) === Number(rd.RentslabId) ||
            Number(dp.RentslabId) === Number(rd.RentslabId)
        );

        return {
          RentslabId: rd.RentslabId,
          rentslab_price: found?.rentslab_price ?? found?.discount ?? "",
        };
      });

      setForm({
        cat_id: String(catId || ""),
        carmodel_id: String(carId || ""),
        season_id: String(seasonId || ""),
        price: basePrice ?? "",
        useDefault: false,
        duration_prices: durationPrices,
      });

      setEditId(rec.tar_id || price.tar_id);
    } catch (error) {
      console.error("startEdit error:", error);

      setFormModal(false);

      toasts.error(
        error.response?.data?.message || "Failed to load price for editing"
      );
    }
  };

  // VIEW

  const handleView = async (price) => {
    try {
      const response = await API.get(`/price/${price.tar_id}`);

      setViewModal(response.data);
    } catch (error) {
      console.error("handleView error:", error);

      setViewModal(price);

      toasts.error("Could not load complete details");
    }
  };

  // GROUP CHANGE

  const handleGroupChange = (catId) => {
    setForm((prev) => ({
      ...prev,
      cat_id: catId,
      carmodel_id: "",
    }));

    loadCars(catId);
  };

  // BASE PRICE

  const handlePriceChange = (value) => {
    setForm((prev) => {
      const updated = {
        ...prev,
        price: value,
      };

      if (prev.useDefault) {
        updated.duration_prices = prev.duration_prices.map((dp) => ({
          ...dp,
          rentslab_price: value,
        }));
      }

      return updated;
    });
  };

  // DURATION PRICE

  const handleDurPrice = (rentslabId, value) => {
    setForm((prev) => ({
      ...prev,

      duration_prices: prev.duration_prices.map((dp) =>
        Number(dp.RentslabId) === Number(rentslabId)
          ? {
              ...dp,
              rentslab_price: value,
            }
          : dp
      ),
    }));
  };

  // SUBMIT

  const handleSubmit = async () => {
    if (!form.cat_id || !form.carmodel_id || !form.season_id || !form.price) {
      toasts.error("Car Group, Car Model, Season and Price/Day are required");

      return;
    }

    setSaving(true);

    try {
      const payload = {
        cat_id: form.cat_id,
        carmodel_id: form.carmodel_id,
        season_id: form.season_id,
        price_perday: form.price,

        duration_prices: form.duration_prices.map((dp) => ({
          RentslabId: dp.RentslabId,
          rentslab_price: dp.rentslab_price,
        })),
      };

      if (editId) {
        await API.put(`/price/${editId}`, payload);

        toasts.success("Price updated successfully");
      } else {
        await API.post("/price", payload);

        toasts.success("Price added successfully");
      }

      await fetchPrices();

      setFormModal(false);
      resetForm();
    } catch (error) {
      console.error("Submit error:", error);

      toasts.error(error.response?.data?.message || "Operation failed");
    } finally {
      setSaving(false);
    }
  };

  // DELETE

  const handleDelete = async () => {
    if (!delModal) return;

    setDelSaving(true);

    try {
      await API.delete(`/price/${delModal.tar_id}`);

      toasts.success("Price deleted successfully");

      setDelModal(null);

      if (prices.length === 1 && page > 1) {
        setPage((p) => p - 1);
      } else {
        await fetchPrices();
      }
    } catch (error) {
      console.error("Delete error:", error);

      toasts.error(error.response?.data?.message || "Delete failed");
    } finally {
      setDelSaving(false);
    }
  };

  // DURATION FORMAT

  const formatDur = (rd) => {
    if (Number(rd.ToRentslab) === 1000) {
      return `${rd.FromRentslab} Days & Above`;
    }

    return `${rd.FromRentslab}–${rd.ToRentslab} Days`;
  };


  // =========================================================
  // PAGINATION - PREVIOUS
  // =========================================================

  const handlePrevious = () => {
    if (
      loading ||
      page <= 1
    ) {
      return;
    }

    setPage(
      (prev) =>
        Math.max(
          1,
          prev - 1
        )
    );
  };

  // =========================================================
  // PAGINATION - NEXT
  // =========================================================

  const handleNext = () => {
    if (
      loading ||
      totalPages <= 0 ||
      page >= totalPages
    ) {
      return;
    }

    setPage(
      (prev) =>
        Math.min(
          totalPages,
          prev + 1
        )
    );
  };

  // RETURN

  return (
    <Layout activePage="Car Price">
      <main className="dashboard-main">
        <div className="table-card">
          {/* =================================================
              HEADER / FILTERS
          ================================================= */}

          <div className="table-card-head">
            {/* CAR GROUP */}
            <select
              value={filterCatId}
              onChange={(e) => {
                setFilterCatId(e.target.value);
                setPage(1);
              }}
              className="season-input"
            >
              <option value="">All Car Groups</option>

              {groups.map((g) => (
                <option key={g.cat_id} value={String(g.cat_id)}>
                  {g.cat_name}
                </option>
              ))}
            </select>

            {/* SEASON */}
            <select
              value={filterSeasonId}
              onChange={(e) => {
                setFilterSeasonId(e.target.value);
                setPage(1);
              }}
              className="season-input"
            >
              <option value="">All Seasons</option>

              {seasons.map((s) => (
                <option key={s.season_id} value={String(s.season_id)}>
                  {s.season_name}
                </option>
              ))}
            </select>

            {/* SEARCH */}
            <div className="search-wrapper">
              <span className="search-icon">
                <FiSearch size={17} />
              </span>

              <input
                type="text"
                placeholder="Search car..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="search-input"
              />

              {search && (
                <button
                  type="button"
                  className="season-clear-btn"
                  onClick={() => {
                    setSearch("");
                    setPage(1);
                  }}
                >
                  ✕
                </button>
              )}
            </div>

            {/* ADD */}
            <button
              type="button"
              className="um-btn-primary"
              onClick={openAddModal}
            >
              <span className="um-add-icon">＋</span>
              Add Car Price
            </button>
          </div>

          {/* =================================================
              TABLE
          ================================================= */}

          <div className="table-responsive">
            <table className="um-table">
              <thead>
                <tr className="um-table-head">
                  <th className="um-th text-center">#</th>
                  <th className="um-th text-center">Car Group</th>
                  <th className="um-th text-center">Car Model</th>
                  <th className="um-th text-center">Season</th>
                  <th className="um-th text-center">Durations</th>
                  <th className="um-th text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 6 }).map((_, j) => (
                        <td key={j} className="um-td text-center">
                          <div className="skeleton" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : prices.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="empty-table-cell text-center py-5"
                    >
                      <div className="empty-icon">
                        <FiCreditCard />
                      </div>

                      <div className="empty-title">
                        {search
                          ? "No matching prices found"
                          : "No prices added yet"}
                      </div>

                      <div className="empty-sub">
                        {search
                          ? "Try another search"
                          : "Use the Add Car Price button to add a price"}
                      </div>
                    </td>
                  </tr>
                ) : (
                  prices.map((p, i) => (
                    <tr
                      key={p.tar_id}
                      className={`um-row ${
                        editId === p.tar_id
                          ? "editing-row"
                          : i % 2 === 0
                          ? "even-row"
                          : "odd-row"
                      }`}
                    >
                      {/* NUMBER */}
                      <td className="um-td text-center">
                        {(page - 1) * limit + i + 1}
                      </td>

                      {/* CAR GROUP */}
                      <td className="um-td text-center">
                        <span className="car-group-badge">
                          {p.cat_name || "—"}
                        </span>
                      </td>

                      {/* CAR MODEL */}
                      <td className="um-td text-center">
                        <span className="car-name-badge">
                          {p.carmodel_name || "—"}
                        </span>
                      </td>

                      {/* SEASON */}
                      <td className="um-td text-center">
                        <span className="season-badge">
                          {p.season_name || "—"}
                        </span>
                      </td>

                      {/* DURATIONS */}
                      <td className="um-td text-center">
                        <button
                          type="button"
                          className="action-btn action-view duration-btn"
                          title="View Duration Pricing"
                          onClick={() => handleView(p)}
                        >
                          {p.duration_prices?.length || 0}
                        </button>
                      </td>

                      {/* ACTION */}
                      <td className="um-td text-center">
                        <div className="action-buttons">
                          {/* VIEW */}
                          <button
                            type="button"
                            className="action-btn action-view"
                            title="View"
                            onClick={() => handleView(p)}
                          >
                            <FiEye size={15} />
                          </button>

                          {/* EDIT */}
                          <button
                            type="button"
                            className="action-btn action-edit"
                            title="Edit"
                            onClick={() => startEdit(p)}
                          >
                            <FiEdit size={15} />
                          </button>

                          {/* DELETE */}
                          <button
                            type="button"
                            className="action-btn action-delete delete-enabled"
                            title="Delete"
                            onClick={() => setDelModal(p)}
                          >
                            <FiTrash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* =================================================
                PAGINATION
            ================================================= */}
               <Pagination
                  page={page}
                  totalPages={totalPages}
                  total={total}
                  limit={10}
                  loading={loading}
                  onPrevious={handlePrevious}
                  onNext={handleNext}
                />
          </div>
        </div>

        {/* =================================================
            ADD / EDIT PRICE MODAL
        ================================================= */}

        {formModal && (
          <div className="um-overlay" onClick={closeFormModal}>
            <div
              className="um-modal um-view-modal"
              onClick={(e) => e.stopPropagation()}
            >
              {/* HEADER */}
              <div className="um-modal-head">
                <div className="d-flex align-items-center gap-2">
                  <div className="form-card-icon">
                    {editId ? <FiEdit size={24} /> : <FiPlus size={24} />}
                  </div>

                  <div>
                    <h2 className="um-modal-title mb-0">
                      {editId ? "Edit Car Price" : "Add Car Price"}
                    </h2>

                    <p className="form-card-sub mb-0">
                      {editId
                        ? "Modify pricing for the selected car and season"
                        : "Set base price and per-duration pricing"}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  className="um-x-btn"
                  onClick={closeFormModal}
                  disabled={saving}
                >
                  ✕
                </button>
              </div>

              {/* BODY */}
              <div className="um-modal-scroll">
                <div className="row g-3">
                  {/* GROUP */}
                  <div className="col-md-3">
                    <label className="field-label">Car Group *</label>

                    <select
                      className="rd-input"
                      value={form.cat_id}
                      onChange={(e) => handleGroupChange(e.target.value)}
                    >
                      <option value="">— Select —</option>

                      {groups.map((g) => (
                        <option key={g.cat_id} value={g.cat_id}>
                          {g.cat_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* CAR */}
                  <div className="col-md-3">
                    <label className="field-label">Car Model Name *</label>

                    <select
                      className="rd-input"
                      value={form.carmodel_id}
                      disabled={!form.cat_id || carsLoading}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          carmodel_id: e.target.value,
                        }))
                      }
                    >
                      <option value="">
                        {carsLoading ? "Loading…" : "— Select —"}
                      </option>

                      {cars.map((c) => (
                        <option key={c.carmodel_id} value={c.carmodel_id}>
                          {c.carmodel_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* SEASON */}
                  <div className="col-md-3">
                    <label className="field-label">Season *</label>

                    <select
                      className="rd-input"
                      value={form.season_id}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          season_id: e.target.value,
                        }))
                      }
                    >
                      <option value="">— Select —</option>

                      {seasons.map((s) => (
                        <option key={s.season_id} value={s.season_id}>
                          {s.season_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* PRICE */}
                  <div className="col-md-3">
                    <label className="field-label">Price / Day *</label>

                    <div className="position-relative">
                      <span
                        className="position-absolute"
                        style={{
                          left: 14,
                          top: "50%",
                          transform: "translateY(-50%)",
                          fontWeight: 800,
                          fontSize: 15,
                          zIndex: 1,
                        }}
                      >
                        ₹
                      </span>

                      <input
                        className="rd-input"
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={form.price}
                        onChange={(e) => handlePriceChange(e.target.value)}
                        style={{
                          paddingLeft: 32,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* DURATION PRICE TABLE */}
                {rentDurs.length > 0 && (
                  <>
                    <div className="sec-div mt-4 mb-3">
                      <span>Price per Rent Duration</span>
                    </div>

                    <div className="season-discount-card">
                      <div className="table-responsive">
                        <table className="table table-bordered table-hover align-middle mb-0">
                          <thead className="table-light um-table-head">
                            <tr>
                              <th>#</th>
                              <th>Duration Slab</th>
                              <th>Range</th>
                              <th style={{ width: "250px" }}>Price (₹)</th>
                            </tr>
                          </thead>

                          <tbody>
                            {rentDurs.map((rd, i) => {
                              const dp = form.duration_prices.find(
                                (d) =>
                                  Number(d.RentslabId) === Number(rd.RentslabId)
                              );

                              const val = dp?.rentslab_price ?? "";

                              const isMore = Number(rd.ToRentslab) === 1000;

                              return (
                                <tr key={rd.RentslabId}>
                                  <td className="text-center">{i + 1}</td>

                                  <td>
                                    <div className="text-center gap-2">
                                      <span className="badge badge-success">
                                        Slab {i + 1}
                                      </span>
                                    </div>
                                  </td>

                                  <td>
                                    <span
                                      className={
                                        isMore
                                          ? "badge bg-warning text-dark"
                                          : "badge badge-success"
                                      }
                                    >
                                      {formatDur(rd)}
                                    </span>
                                  </td>

                                  <td>
                                    <div className="input-group">
                                      <span className="input-group-text">
                                        ₹
                                      </span>

                                      <input
                                        type="number"
                                        className="form-control"
                                        min="0"
                                        step="0.01"
                                        placeholder="0.00"
                                        value={val}
                                        onChange={(e) =>
                                          handleDurPrice(
                                            rd.RentslabId,
                                            e.target.value
                                          )
                                        }
                                      />
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* FOOTER */}
              <div className="um-modal-foot">
                <button
                  type="button"
                  className="um-btn-secondary"
                  onClick={closeFormModal}
                  disabled={saving}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="um-btn-primary"
                  onClick={handleSubmit}
                  disabled={saving}
                >
                  {saving ? (
                    <span className="spinner" />
                  ) : editId ? (
                    <>
                      <FiSave />
                      Update Price
                    </>
                  ) : (
                    <>
                      <FiPlus />
                      Add Price
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* =================================================
            VIEW MODAL
        ================================================= */}

        {viewModal && (
          <div className="um-overlay" onClick={() => setViewModal(null)}>
            <div
              className="um-modal um-view-modal"
              onClick={(e) => e.stopPropagation()}
            >
              {/* HEADER */}
              <div className="um-modal-head">
                <div className="d-flex align-items-center gap-2">
                  <h2 className="um-modal-title d-flex align-items-center gap-2 mb-0">
                    <FiCreditCard size={24} />
                    Price Details
                  </h2>

                  <p className="form-card-sub mb-0">
                    (Car pricing and duration details)
                  </p>
                </div>

                <button
                  type="button"
                  className="um-x-btn"
                  onClick={() => setViewModal(null)}
                >
                  ✕
                </button>
              </div>

              {/* BODY */}
              <div className="um-modal-scroll">
                <div className="sec-div mb-3">
                  <span>Car Price Details</span>
                </div>

                <div className="row g-3 mb-4">
                  <div className="col-md-3">
                    <div className="bg-light rounded-3 border p-3 h-100">
                      <div className="small text-muted text-uppercase fw-semibold mb-1">
                        Car Group
                      </div>

                      <div className="fw-bold">{viewModal.cat_name || "—"}</div>
                    </div>
                  </div>

                  <div className="col-md-3">
                    <div className="bg-light rounded-3 border p-3 h-100">
                      <div className="small text-muted text-uppercase fw-semibold mb-1">
                        Car Model
                      </div>

                      <div className="fw-bold">
                        {viewModal.carmodel_name || "—"}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-3">
                    <div className="bg-light rounded-3 border p-3 h-100">
                      <div className="small text-muted text-uppercase fw-semibold mb-1">
                        Season
                      </div>

                      <div className="fw-bold">
                        {viewModal.season_name || "—"}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-3">
                    <div className="bg-light rounded-3 border p-3 h-100">
                      <div className="small text-muted text-uppercase fw-semibold mb-1">
                        Base Price / Day
                      </div>

                      <div className="fw-bold">
                        ₹
                        {Number(
                          viewModal.price_perday || viewModal.price || 0
                        ).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="sec-div mb-3">
                  <span>Duration Pricing</span>
                </div>

                {viewModal.duration_prices?.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-bordered table-hover align-middle mb-0">
                      <thead className="table-light um-table-head">
                        <tr>
                          <th style={{ width: "55%" }}>Duration Range</th>

                          <th style={{ width: "45%" }}>Price / Day</th>
                        </tr>
                      </thead>

                      <tbody>
                        {viewModal.duration_prices.map((dp, i) => {
                          const curr = Number(
                            dp.rentslab_price || dp.discount || 0
                          );

                          const isMore = Number(dp.ToRentslab) === 1000;

                          return (
                            <tr key={i}>
                              <td className="text-center">
                                <span
                                  className={`badge ${
                                    isMore ? "text-bg-warning" : "badge-success"
                                  }`}
                                >
                                  {isMore
                                    ? `${dp.FromRentslab} Days & Above`
                                    : `${dp.FromRentslab}–${dp.ToRentslab} Days`}
                                </span>
                              </td>

                              <td className="text-center">
                                <span className="fw-bold">
                                  ₹{curr.toLocaleString()}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center text-muted bg-light rounded p-4">
                    <div className="fs-4 mb-2">
                      <FiCreditCard />
                    </div>

                    <div>No duration pricing configured</div>
                  </div>
                )}
              </div>

              {/* FOOTER */}
              <div className="um-modal-foot">
                <button
                  type="button"
                  className="um-btn-secondary"
                  onClick={() => setViewModal(null)}
                >
                  Close
                </button>

                <button
                  type="button"
                  className="um-btn-primary"
                  onClick={() => {
                    const data = viewModal;

                    setViewModal(null);

                    setTimeout(() => {
                      startEdit(data);
                    }, 100);
                  }}
                >
                  <FiEdit />
                  Edit Price
                </button>
              </div>
            </div>
          </div>
        )}

        {/* =================================================
            DELETE MODAL
        ================================================= */}

        {delModal && (
          <div
            className="um-overlay"
            onClick={() => {
              if (!delSaving) {
                setDelModal(null);
              }
            }}
          >
            <div
              className="um-modal um-delete-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="delete-content">
                {/* DELETE ICON */}
                <div className="delete-icon">
                  <FiTrash2 />
                </div>

                {/* TITLE */}
                <h2 className="delete-title">Delete Price?</h2>

                {/* PRICE DETAILS */}
                <div className="delete-badges">
                  <span className="badge badge-success">
                    {delModal.carmodel_name}
                  </span>
                  &nbsp;
                  <span className="badge badge-success">
                    {delModal.season_name}
                  </span>
                  &nbsp;
                  <span className="badge badge-success">
                    ₹{delModal.price_perday}/day
                  </span>
                </div>

                {/* DESCRIPTION */}
                <p className="delete-description">
                  This will also delete all duration pricing for this entry.
                  <br />
                  <strong>This action cannot be undone.</strong>
                </p>

                {/* ACTIONS */}
                <div className="delete-actions">
                  <button
                    type="button"
                    className="um-btn-secondary"
                    onClick={() => setDelModal(null)}
                    disabled={delSaving}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    disabled={delSaving}
                    className="delete-confirm-btn"
                    onClick={handleDelete}
                  >
                    {delSaving ? (
                      <span className="spinner" />
                    ) : (
                      <>
                        <FiTrash2 size={16} />
                        Delete Permanently
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TOAST*/}

   
      </main>
    </Layout>
  );
}
